import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { addSubCategory, fetchCategoriesService, updateSubCategory } from "../../../Services/categoryService";
import CustomTextInputBasic from "../../../components/common/CustomTextInput/CustomTextInputBasic";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import CustomSelect from "../../../components/common/CustomSelect";
import { toast } from "react-toastify";
import FileInput from "../../../components/common/FileInput";
import { getAllCategoriesUrl } from "../../../utils/urls";
import { errorToast } from "../../../utils/toasts";

export default function AddSubCategoryForm({categories, onCancel, onSave, initialValues = null }) {

  const [categoriesList, setCategoriesList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      fetchCategoriesService().then((response) => {
        let _list = response.filter((cat) => cat.is_active && !cat.is_delete && !cat.is_lead_add_without_product);
        setCategoriesList(_list);
      }).catch((error) => {
        console.error("Error fetching categories:", error);
        errorToast("Failed to fetch categories");
      }).finally(() => {
        setLoading(false);
      })
    }
    if (categoriesList.length === 0) {
      fetchCategories();
    }
  }, [])
  
  // console.log("categoriesList", categoriesList);
  
 
  return (
    <div className="flex flex-col flex-1">
      {/* Header */}
      <div className="flex items-center gap-6">
        <button className="border p-[9px] rounded-[8px] w-10 h-10" onClick={onCancel}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-semibold text-[#002C66]">
          {initialValues ? "Edit Product Subcategory" : "Add Product Subcategory"}
        </h1>
      </div>

      {/* Form */}
      <Formik
        enableReinitialize
        initialValues={{
          title: initialValues?.title || "",
          description: initialValues?.description || "",
          category_id: initialValues?.category_id || "", // For both add and edit
          sub_category_id: initialValues?.sub_category_id || "", // For edit only
          file: initialValues?.logo_url || null,
        }}
        validationSchema={Yup.object({
          title: Yup.string()
            .trim()
            .required("Subcategory name is required")
            .min(3, "Subcategory name must be at least 3 characters")
            .max(255, "Subcategory name cannot exceed 255 characters")
            .matches(/^[a-zA-Z0-9\s]*$/, "Special characters are not allowed"),
          description: Yup.string()
            .trim()
            .required("Description is required")
            .min(3, "Description must be at least 3 characters")
            .max(255, "Description cannot exceed 255 characters"),
          category_id: Yup.string().required("Category selection is required"),


          file: initialValues ? Yup.mixed()
            .optional()
            .test("fileSize", "Logo must be less than 2MB", (value) => {
              if (!value) return true;
              if (typeof value === "string") return true;
              return value.size <= 2 * 1024 * 1024;
            })
            .test("fileType", "Only image files are allowed", (value) => {
              if (!value) return true;
              if (typeof value === "string") return true;
              return value.type.startsWith("image/");
            })
            : Yup.mixed()
            .required("Logo is required")
            .test("fileSize", "Logo must be less than 2MB", (value) => {
              if (!value) return true;
              return value.size <= 2 * 1024 * 1024;
            })
            .test("fileType", "Only image files are allowed", (value) => {
              if (!value) return true;
              return value.type.startsWith("image/");
            }),

        })}
        onSubmit={async (values, { setSubmitting }) => {
          console.log("Form Values:", values);
          try {
            const formData = new FormData();
            formData.append('title', values.title.trim());
            
            formData.append('description', values.description.trim());
            formData.append('category_id', values.category_id.trim());
            console.log("Form Data:", formData);
            
            if (typeof values.file === "object" && values.file) {
              formData.append('file', values.file);
            }

            let response;
            if (initialValues && initialValues.sub_category_id) {
              // Update the existing subcategory
              formData.append('sub_category_id', values.sub_category_id.trim());
              response = await updateSubCategory(formData);
              toast.success("Sub-category updated successfully!");
            } else {
              // Add a new subcategory
              response = await addSubCategory(formData);
              toast.success("Sub-category added successfully!");
            }
            onSave(response);
          } catch (error) {
            console.error("Failed to save subcategory:", error);
            toast.error("Failed to save subcategory. Please try again.");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, handleChange, handleBlur, errors, touched, isSubmitting, isValid,dirty, setFieldValue }) => (
          <Form className="flex-1 p-4 gap-6 border rounded-lg bg-white mt-4">
            <h2 className="text-xl font-semibold">Basic Details</h2>

            <div className="grid grid-cols-2 justify-center items-center gap-4">
              <div>
                <CustomTextInputBasic
                  label="Subcategory Name"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter"
                  required
                  error={touched.title && !!errors.title}
                  errorMessage={errors.title}
                />
              </div>

              <div>
                <CustomSelect
                  label="Select category"
                  name="category_id"
                  placeholder="Select Category"
                  menuList={categoriesList.map((cat) => ({
                    value: cat.category_id,
                    label: cat.title,
                  }))}
                  value={values.category_id}
                  onChange={handleChange}
                />
              </div>

              <div className="w-full col-span-2">
                <CustomTextInputBasic
                  label="Description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter"
                  error={touched.description && !!errors.description}
                  errorMessage={errors.description}
                />
              </div>

              <div className="w-full col-span-2">
                {initialValues?.logo_url && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Current Logo</p>
                    <div className="w-20 h-20 border rounded-lg overflow-hidden">
                      <img 
                        src={initialValues.logo_url} 
                        alt="Current Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                )}
                <FileInput
                  name="file"
                  inputLabel="Subcategory Logo"
                  fileType="image"
                  size={2}
                  sizeVariant="MB"
                  setSelectedFiles={(files) => {
                    setFieldValue("file", files[0]);
                  }}
                />
               
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 flex justify-end space-x-3">
              <CustomButton label="Cancel" variant="outlined" onClick={onCancel} />
              <CustomButton label={initialValues ? "Update" : "Save"} variant="contained" type="submit" disabled={isSubmitting|| !dirty}
                className={`${
                  isSubmitting || !dirty ? "!opacity-50 !cursor-not-allowed" : ""
                }`} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
